// Composition hook over `useCurrentUser` (state/data/account). Drives the
// session-extend keep-alive timer as a mount-scoped side effect.
//
// CALL ONCE per app session — typically from App.tsx. A second `useAuth()`
// in the tree would start a parallel `sessionExtend` timer chain.

import { useEffect } from 'react';
import { useCurrentUser } from '@/state/data/account';
import AccountService, { type AccountData, type UserData } from '@/services/AccountService';
import { WsError } from '@/lib/wiservice';
import { logout } from '@/lib/account';
import { isUsableTtl, nextExtendDelayMs } from '@/logic/sessionKeepAlive';
import {
  AUTH_ERROR_CODES,
  SESSION_EXTEND_REQUESTED_TTL_SECONDS,
  SESSION_EXTEND_MIN_DELAY_MS,
} from '@/config/constants';

export interface UseAuthReturn {
  account: AccountData | null;
  currentUser: UserData | null;
  isLoading: boolean;
  isWithinger: boolean;
  initDone: boolean;
  error: Error | null;
}

function isAuthError(err: unknown): err is WsError {
  return err instanceof WsError && (AUTH_ERROR_CODES as readonly number[]).includes(err.status);
}

export function useAuth(): UseAuthReturn {
  const query = useCurrentUser();

  // Session-extend keep-alive. Each extend asks for an hour and the NEXT tick
  // is scheduled from the TTL apiv2 actually granted, so the cadence tracks the
  // cohort's real session length (~32 min for an RPM coordinator) instead of a
  // client-side guess. The first extend fires immediately at mount — it is the
  // only thing that tells us how long this session lives. Bound to the mount of
  // whichever component first hosts useAuth (App.tsx). Dep is the accountid
  // primitive (not the object reference) so the effect doesn't relaunch on
  // TanStack structural-sharing rerenders.
  const accountId = query.data?.account?.accountid;
  useEffect(() => {
    if (accountId == null) return undefined;

    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const extendThenSchedule = async (): Promise<void> => {
      if (cancelled) return;
      // Degraded default: retry at the floor rather than going quiet.
      let delayMs = SESSION_EXTEND_MIN_DELAY_MS;
      try {
        const body = await AccountService.sessionExtend({
          ttl: SESSION_EXTEND_REQUESTED_TTL_SECONDS,
        });
        const grantedTtl = body?.ttl;
        if (!isUsableTtl(grantedTtl)) {
          console.warn('[session] extend returned no usable ttl', { grantedTtl });
        }
        delayMs = nextExtendDelayMs(grantedTtl);
      } catch (err) {
        // Package would already have redirected on a true auth error.
        // Reaching here = non-redirected case (ratelimit, network blip).
        // If the WsError still carries an auth status, log out cleanly.
        if (isAuthError(err)) {
          logout();
          return;
        }
        console.warn('[session] extend failed, retrying', err);
      }
      if (cancelled) return;
      timer = setTimeout(extendThenSchedule, delayMs);
    };

    extendThenSchedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [accountId]);

  return {
    account: query.data?.account ?? null,
    currentUser: query.data?.currentUser ?? null,
    isLoading: query.isLoading,
    isWithinger: query.data?.isWithinger ?? false,
    // `initDone` semantics from the previous Zustand store: first attempt
    // has settled (success OR error). TanStack's isLoading is false in both.
    initDone: !query.isLoading,
    error: query.error ?? null,
  };
}

export default useAuth;
